<template>
  <form
    method="POST"
    class="form"
    novalidate
    v-bind="$attrs"
    @submit.prevent="submit"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <div
      v-if="message"
      v-html="message"
      class="form__message"
    />
    <div class="form__fields">
      <div
        v-for="item in fields"
        :key="`form_field_${item.name}`"
        class="form__field"
      >
        <component
          :ref="`field_${item.name}`"
          :is="item.is || 'BaseField'"
          :value="values[item.name]"
          v-bind="item"
          v-on="item.on"
          @input="setFieldValue(item.name, $event)"
        />
      </div>
      <slot name="fields" />
    </div>
    <slot name="content" />
    <div class="form__actions" :class="`form__actions_${alignActions}`">
      <BaseButton
        v-for="(item, i) in actions"
        :key="`form_action_${i}`"
        v-bind="item"
        class="form__action"
        @click="item.action ? item.action() : null"
      />
      <slot name="actions" />
    </div>
    <slot />
  </form>
</template>

<script>
import BaseField from '@/components/BaseField.vue';
import BaseButton from '@/components/BaseButton.vue';

export default {
  name: 'BaseForm',
  components: {
    BaseField,
    BaseButton,
  },
  props: {
    values: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    actions: {
      type: Array,
      default() {
        return [
          {
            type: 'submit',
            text: this.$t('actions.send'),
          },
        ];
      },
    },
    alignActions: {
      type: String,
      default: 'center',
    },
  },
  data() {
    return {
      message: '',
    };
  },
  methods: {
    setMessage(message) {
      this.message = message;
    },
    setFieldValue(name, value) {
      this.$set(this.values, name, value);
    },
    setFieldFocusByName(name) {
      const field = this.getFieldByName(name);
      return field?.focus?.();
    },
    getFieldByName(name) {
      const [firstComponent] = this.$refs[`field_${name}`] || [];
      return firstComponent;
    },
    getFieldValueByName(name) {
      return this.values[name];
    },
    checkFieldValidityByName(name) {
      const field = this.getFieldByName(name);
      return field?.checkValidity?.();
    },
    setFocus() {
      const [firstField] = this.fields;
      return this.setFieldFocusByName(firstField?.name);
    },
    checkFormComplete() {
      return this.fields.every(({ name }) => {
        const field = this.getFieldByName(name);
        if (field.checkValidity()) {
          return true;
        }
        this.setFieldFocusByName(name);
        return false;
      });
    },
    onFocusIn(e) {
      const { name } = e?.target || {};
      const field = this.getFieldByName(name);
      if (field) {
        this.$emit('focusin', field, name);
      }
    },
    onFocusOut(e) {
      const { name } = e?.target || {};
      const field = this.getFieldByName(name);
      if (field) {
        this.$emit('focusout', field, name);
      }
    },
    submit() {
      // При отправке формы лучше сбросить фокус у активного поля
      document.activeElement.blur();

      if (this.checkFormComplete()) {
        this.setMessage('');
        this.$emit('submit', this.values);
      }
    },
  },
};
</script>

<style lang="stylus">
.form
  display block

  &__message
    display block
    padding 1em
    font-size 0.8em
    color $c_error
    text-align center
    background-color alpha($c_error, 15%)
    border-radius 0.2em
    margin-bottom 2em

  &__field
    margin-top 0.85em
    &:first-child
      margin-top 0

  &__terms
    display block
    font-size 0.86em
    line-height 1.4
    padding 1em 2em 0 0
    &-link
      font-size 1em
      background none
      outline none
      padding 0
      margin 0
      border 0
      color $c_text_link

  &__actions
    margin-top 1.25em
    &_fit
      display flex
    &_center
      text-align center
    &_left
      text-align left
    &_right
      text-align right

  &__action
    ~/__actions_fit &
      flex-grow 1
      flex-basis 0
      max-width 100%
      margin-left 1.5em
      &:first-child
        margin-left 0

    ~/__actions_center &
      margin-left 0.75em
      margin-right @margin-left

    ~/__actions_left &
      margin-left 1.5em
      &:first-child
        margin-left 0

    ~/__actions_right &
      margin-right 1.5em
      &:last-child
        margin-right 0
</style>
